import { createSelector } from '@reduxjs/toolkit';
// ===== BASE SELECTORS =====
export const selectAnalytics = (state) => state.analytics;
export const selectAnalyticsData = (state) => state.analytics.data;
export const selectAnalyticsLoading = (state) => state.analytics.isLoading;
export const selectAnalyticsError = (state) => state.analytics.error;
export const selectAnalyticsLastUpdated = (state) => state.analytics.lastUpdated;
// ===== COMPUTED SELECTORS =====
// Visitors chart data
export const selectVisitorsChartData = createSelector([selectAnalyticsData], (data) => {
    if (!data?.visitors)
        return null;
    return {
        categories: data.visitors.map(v => v.date),
        series: [{
                name: 'Visitantes',
                data: data.visitors.map(v => v.value)
            }]
    };
});
// Devices chart data
export const selectDevicesChartData = createSelector([selectAnalyticsData], (data) => {
    if (!data?.devices)
        return null;
    return {
        categories: data.devices.map(d => d.device),
        series: [{
                name: 'Dispositivos',
                data: data.devices.map(d => d.value)
            }]
    };
});
// Traffic sources chart data
export const selectTrafficSourcesChartData = createSelector([selectAnalyticsData], (data) => {
    if (!data?.trafficSources)
        return null;
    return {
        categories: data.trafficSources.map(t => t.source),
        series: [{
                name: 'Visitas',
                data: data.trafficSources.map(t => t.value)
            }]
    };
});
// Page views chart data
export const selectPageViewsChartData = createSelector([selectAnalyticsData], (data) => {
    if (!data?.topPages)
        return null;
    return {
        categories: data.topPages.map(p => p.page),
        series: [
            {
                name: 'Visualizações',
                data: data.topPages.map(p => p.views)
            },
            {
                name: 'Visualizações Únicas',
                data: data.topPages.map(p => p.uniqueViews)
            }
        ]
    };
});
// Conversions chart data
export const selectConversionsChartData = createSelector([selectAnalyticsData], (data) => {
    if (!data?.conversions)
        return null;
    return {
        categories: data.conversions.map(c => c.source),
        series: [
            {
                name: 'Visitantes',
                data: data.conversions.map(c => c.visitors)
            },
            {
                name: 'Conversões',
                data: data.conversions.map(c => c.conversions)
            }
        ]
    };
});
// Analytics summary stats
export const selectAnalyticsSummary = createSelector([selectAnalyticsData], (data) => {
    if (!data)
        return null;
    return {
        totalPageViews: data.pageViews,
        uniqueVisitors: data.uniqueVisitors,
        bounceRate: data.bounceRate,
        avgSessionDuration: data.avgSessionDuration,
        totalDevices: data.devices.reduce((sum, d) => sum + d.value, 0),
        totalTrafficSources: data.trafficSources.reduce((sum, t) => sum + t.value, 0),
    };
});
// Top performing metrics
export const selectTopMetrics = createSelector([selectAnalyticsData], (data) => {
    if (!data)
        return null;
    return {
        topDevice: data.devices.reduce((prev, current) => prev.value > current.value ? prev : current),
        topTrafficSource: data.trafficSources.reduce((prev, current) => prev.value > current.value ? prev : current),
        topPage: data.topPages.reduce((prev, current) => prev.views > current.views ? prev : current),
        bestConversion: data.conversions.reduce((prev, current) => prev.rate > current.rate ? prev : current),
    };
});
// Analytics status
export const selectAnalyticsStatus = createSelector([selectAnalyticsLoading, selectAnalyticsError, selectAnalyticsData], (isLoading, error, data) => ({
    isLoading,
    hasError: !!error,
    hasData: !!data,
    isEmpty: !data || Object.keys(data).length === 0,
}));
